// Pure schema/answer builders (no React) — also loaded by scripts/prerender-seo.js at build time.
import { fmtTime, inr } from "./format";

export const SITE_URL = "https://youngspinetricity.com";
const CLINIC_ID = `${SITE_URL}/#clinic`;
const DOCTOR_ID = `${SITE_URL}/about#physician`;
const WEBSITE_ID = `${SITE_URL}/#website`;

export function hoursSummary(schedule = []) {
  const open = schedule.filter((d) => d.open);
  if (!open.length) return "By appointment only";
  const same = open.every((d) => d.start === open[0].start && d.end === open[0].end);
  const range = `${fmtTime(open[0].start)} – ${fmtTime(open[0].end)}`;
  if (same && open.length === 7) return `${range}, all 7 days`;
  if (same) return `${range} (${open.map((d) => d.day.slice(0, 3)).join(", ")})`;
  return open.map((d) => `${d.day.slice(0, 3)} ${fmtTime(d.start)}–${fmtTime(d.end)}`).join(" · ");
}

export function fillTemplate(text, vars) {
  return text.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "").replace(/\s+\./g, ".").replace(/\.{2,}/g, ".").trim();
}

export function quickAnswers(templates, s, c, conditionTitles) {
  const vars = {
    address: s.address, hours: hoursSummary(s.schedule), hours_note: s.hours_note, phone_display: s.phone_display,
    fee: inr(s.consultation_fee), doctor_name: c.doctor_name, doctor_designation: c.doctor_designation,
    doctor_credentials: c.doctor_credentials, doctor_registration: c.doctor_registration, doctor_experience: c.doctor_experience,
    conditions: conditionTitles.map((t) => t.toLowerCase()).join("; "),
  };
  return templates.map((t) => ({ id: `quick-${t.id}`, question: t.q, answer: fillTemplate(t.a, vars) }));
}

const postalAddress = (s) => ({
  "@type": "PostalAddress",
  streetAddress: s.address,
  addressLocality: "SAS Nagar (Mohali)",
  addressRegion: "Punjab",
  postalCode: "160059",
  addressCountry: "IN",
});

export function clinicSchema(s, { conditions = [], services = [] } = {}) {
  return {
    "@type": "MedicalClinic",
    "@id": CLINIC_ID,
    name: "Young Spine Tricity",
    alternateName: "Young Spine",
    description: "Advanced Spine Care & Pain Relief Clinic in Mohali offering conservative, non-surgical care (no injections, no surgery) for back pain, neck pain, slip disc, sciatica and posture-related problems.",
    slogan: "Healthy Spine, Healthy Life.",
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/images/logo.jpg`,
    image: `${SITE_URL}/images/og-image.jpg`,
    telephone: s.phone,
    email: s.email,
    address: postalAddress(s),
    hasMap: s.map_link,
    medicalSpecialty: "https://schema.org/Musculoskeletal",
    ...(s.consultation_fee ? { priceRange: `${inr(s.consultation_fee)} consultation` } : {}),
    currenciesAccepted: "INR",
    areaServed: ["Mohali", "Chandigarh", "Panchkula"].map((name) => ({ "@type": "City", name })),
    openingHoursSpecification: (s.schedule || [])
      .filter((d) => d.open)
      .map((d) => ({ "@type": "OpeningHoursSpecification", dayOfWeek: `https://schema.org/${d.day}`, opens: d.start, closes: d.end })),
    knowsAbout: conditions,
    availableService: services.map((name) => ({ "@type": "MedicalTherapy", name })),
    employee: { "@id": DOCTOR_ID },
    sameAs: [s.instagram_url || (s.instagram_handle ? `https://www.instagram.com/${s.instagram_handle}/` : ""), s.facebook_url, s.youtube_url].filter(Boolean),
  };
}

export function physicianSchema(s, c, image = "") {
  const reg = (c.doctor_registration || "").match(/\d+/);
  return {
    "@type": "Physician",
    "@id": DOCTOR_ID,
    name: c.doctor_name,
    description: `${c.doctor_designation}. ${c.doctor_credentials}. ${c.doctor_experience || ""}`.trim(),
    url: `${SITE_URL}/about`,
    ...(image ? { image } : {}),
    medicalSpecialty: "https://schema.org/Musculoskeletal",
    telephone: s.phone,
    address: postalAddress(s),
    ...(reg ? { identifier: { "@type": "PropertyValue", propertyID: "Punjab Medical Council Reg. No.", value: reg[0] } } : {}),
    parentOrganization: { "@id": CLINIC_ID },
  };
}

export const websiteSchema = () => ({
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: "Young Spine Tricity",
  inLanguage: "en-IN",
  publisher: { "@id": CLINIC_ID },
});

export const breadcrumbSchema = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE_URL}${path}` })),
});

export const faqSchema = (faqs) => ({
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
});

export const medicalPageSchema = (path, name, description, conditions) => ({
  "@type": "MedicalWebPage",
  "@id": `${SITE_URL}${path}#webpage`,
  url: `${SITE_URL}${path}`,
  name,
  description,
  inLanguage: "en-IN",
  isPartOf: { "@id": WEBSITE_ID },
  audience: "https://schema.org/Patient",
  about: conditions.map((c) => ({ "@type": "MedicalCondition", name: c.title, description: c.overview })),
});

export function productSchema(p, s, images, withOffer = true) {
  const url = `${SITE_URL}/shop/${p.slug}`;
  const node = {
    "@type": "Product",
    "@id": `${url}#product`,
    name: p.name,
    description: [p.subtitle, (p.description || "").split(/\n\s*\n/)[0]].filter(Boolean).join(". "),
    sku: p.sku || p.slug,
    url,
    image: images,
    category: p.category,
    manufacturer: { "@type": "Organization", name: (p.manufacturer || "").split(",")[0] },
    countryOfOrigin: p.country_of_origin,
  };
  if (!withOffer) return node;
  const threshold = Number(s.free_shipping_threshold) || 0;
  const shipping = threshold && p.price >= threshold ? 0 : Number(s.shipping_charge) || 0;
  node.offers = {
    "@type": "Offer",
    url,
    price: p.price,
    priceCurrency: "INR",
    availability: p.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    itemCondition: "https://schema.org/NewCondition",
    ...(p.on_sale && p.sale_ends_at ? { priceValidUntil: String(p.sale_ends_at).slice(0, 10) } : {}),
    seller: { "@type": "Organization", name: (p.seller || "Amar Healthcare Solutions").split(",")[0] },
    shippingDetails: {
      "@type": "OfferShippingDetails",
      shippingRate: { "@type": "MonetaryAmount", value: shipping, currency: "INR" },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "IN" },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 2, unitCode: "DAY" },
        transitTime: { "@type": "QuantitativeValue", minValue: 5, maxValue: 8, unitCode: "DAY" },
      },
    },
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "IN",
      returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      merchantReturnLink: `${SITE_URL}/refund-and-cancellation-policy`,
    },
  };
  return node;
}

export const graph = (...nodes) => ({ "@context": "https://schema.org", "@graph": nodes.filter(Boolean) });
